import { observer } from "mobx-react-lite";
import Layout from "../../components/layout/Layout";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../stores/root-store-context";
import { useEffect, useState } from "react";
import { Button, Card, Row, Col, Divider, Spin, App, Popconfirm, Modal, Typography, Select, Form, Tabs, DatePicker } from "antd";
import { Test, TestAttempt, UserTestAssignment } from "../../stores/TestsStore";
import { DeleteOutlined, EditOutlined, PlayCircleOutlined, UserAddOutlined, QuestionCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { TestUsersInfo } from "../../components/test/TestUsersInfo";
import { UserTestInfo } from "../../components/test/UserTestInfo";
import dayjs, { Dayjs } from 'dayjs';
import { getDateLocale } from '../../helpers/dateLocalization';
import { SelectProps } from "antd/lib";
import { Roles } from "../../enums/Roles";
import { Languages } from "../../enums/Languages";
import { AssignmentStatus } from "../../enums/AssignmentStatus";

const { Text } = Typography;
const { TabPane } = Tabs;

const Tests = observer(() => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [publicTests, setPublicTests] = useState<Test[]>([]);
    const [filteredPublicTests, setFilteredPublicTests] = useState<Test[]>([]);
    const [companyTests, setCompanyTests] = useState<Test[]>([]);
    const [allTest, setAllTest] = useState<Test[]>([]);
    const [filteredCompanyTests, setFilteredCompanyTests] = useState<Test[]>([]);
    const [userAssignments, setUserAssignments] = useState<UserTestAssignment[]>([]);
    const [companyAssignments, setCompanyAssignments] = useState<UserTestAssignment[]>([]);
    const { applicationStore, apiStore } = useStore();
    const { testsStore } = useStore();
    const NUMBER_MIN_SHOW_TESTS = 10;
    const [loading, setLoading] = useState(false);
    const { message } = App.useApp();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [modalTest, setModalTest] = useState<Test | null>();
    const [selectedLevels, setSelectedLevels] = useState<string[]>(["junior", "middle", "senior"]);
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
    const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
    const dateLocale = getDateLocale(i18n.language);
    const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
    const [visibleTestsAllPublic, setVisibleTestsAllPublic] = useState(NUMBER_MIN_SHOW_TESTS);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            if (!testsStore.isInitialized) {
                await testsStore.initializeStore();
            }

            const publicTests: Test[] = testsStore.testsPublic;
            const companyTests: Test[] = testsStore.testsCompany;
            const userAssignments: UserTestAssignment[] = testsStore.testCompanyAssignments.filter(ta => ta.assignedToUserId == Number(applicationStore.user.id));

            if (applicationStore.user?.roles?.some(r => (r.id === Roles.TestManager || r.id === Roles.Owner || r.id === Roles.Financier || r.id === Roles.Admin))) {
                setCompanyAssignments(testsStore.testCompanyAssignments);
            }

            const filteredPublicTests = publicTests.filter(publicTest =>
                !companyTests.some(companyTest =>
                    companyTest.isActive &&
                    companyTest.level == publicTest.level &&
                    companyTest.skillId == publicTest.skillId
                )
            );

            setAllTest([...filteredPublicTests, ...companyTests]);
            setUserAssignments(userAssignments);
            setCompanyTests(companyTests);
            setFilteredCompanyTests(companyTests);
            setPublicTests(filteredPublicTests);
            setFilteredPublicTests(filteredPublicTests);
            setLoading(false);
        };

        fetchData();
    }, [testsStore]);

    useEffect(() => {
        setCurrentLanguage(i18n.language);
    }, [i18n.language]);

    const loadMoreAllPublicTests = () => {
        setVisibleTestsAllPublic(prevVisibleTests => prevVisibleTests + NUMBER_MIN_SHOW_TESTS);
    };

    const showModal = (test: Test) => {
        setIsModalVisible(true);
        setSelectedUsers([]);
        setSelectedDate(null);
        setModalTest(test);
        setSelectedLanguage(test.language == "all" ? Languages.ANY : test.language);
    };

    const handleEditNavigate = (id: number) => {
        navigate(`/${applicationStore.company.Tenant}/tests/create/${id}/${true}`);
    };

    const handleCancel = () => {
        setSelectedUsers([]);
        setSelectedDate(null);
        setModalTest(null);
        setIsModalVisible(false);
    };

    const handleLevelChange = (value: string[]) => {
        setSelectedLevels(value);

        if (value.length == 0) {
            setFilteredCompanyTests(companyTests);
            setFilteredPublicTests(publicTests);
        }
        else {
            setFilteredCompanyTests(companyTests.filter(t => value.includes(t.level ?? '')));
            setFilteredPublicTests(publicTests.filter(t => value.includes(t.level ?? '')));
        }
    };

    const handleSkillChange = (value: string[]) => {
        setSelectedSkills(value);

        if (value.length == 0) {
            setFilteredCompanyTests(companyTests);
            setFilteredPublicTests(publicTests);
        }
        else {
            setFilteredCompanyTests(companyTests.filter(t => {
                const skillIdStr = String(t.skillId ?? '');
                const isMatch = value.some(v => v == skillIdStr);
                return isMatch;
            }));

            setFilteredPublicTests(publicTests.filter(t => {
                const skillIdStr = String(t.skillId ?? '');
                const isMatch = value.some(v => v == skillIdStr);
                return isMatch;
            }));
        }
    };

    const handleLanguageChange = (value: string) => {
        setSelectedLanguage(value);
    };

    const handleAssign = async () => {
        setLoading(true);

        if (selectedUsers.length == 0) {
            message.error(t("tests.selectUsers"));
            return;
        }

        if (selectedDate == null) {
            message.error(t("tests.selectFinishDate"));
            return;
        }

        if (selectedLanguage == null) {
            message.error(t("tests.selectLanguageWarning"));
            return;
        }

        let resultOperation = true;

        for (const userSelected of selectedUsers) {

            let finishDateTime = new Date().toISOString();

            if (selectedDate != null) {
                const combinedDateTime = selectedDate
                    .hour(3)
                    .minute(0)
                    .second(0)
                    .millisecond(0);

                finishDateTime = combinedDateTime.toISOString();
            }
            else {
                let createdDateTime = new Date();
                createdDateTime.setDate(createdDateTime.getDate() + 2);
                finishDateTime = createdDateTime.toISOString()
            }

            const addAssignment: UserTestAssignment = {
                id: 0,
                test: null,
                language: selectedLanguage,
                testId: modalTest?.id ?? 0,
                numberOfAttempts: modalTest?.numberOfAttempts ?? 2,
                finishAssignedDate: finishDateTime,
                startAssignedDate: new Date().toISOString(),
                assignedDate: new Date().toISOString(),
                assignmentStatusId: 1,
                assignedToUser: null,
                assignmentStatus: null,
                assignedToUserId: Number(userSelected),
                assignedByUserId: Number(applicationStore.user.id),
                testAttempts: []
            };

            try {
                const result = await testsStore.addAssignment(addAssignment);

                if (result == null) {
                    resultOperation = false;
                    break;
                };

                if (applicationStore.user.id == userSelected.toString()) {
                    setUserAssignments(prevState => [...prevState, result]);
                }

            } catch (error) {
                resultOperation = false;
                break;
            }
        }

        const allCompanyTestAssignments = await testsStore.getAllAssignmentByCompanyId(applicationStore.user.companyId.toString());
        setCompanyAssignments(allCompanyTestAssignments);

        setTimeout(() => {
            if (resultOperation) {
                message.success(t("Success"));
            } else {
                message.error(t("Error"));
            }
        }, 0);

        handleCancel();
        setLoading(false);
    };

    const handleUserChange = (value: number[]) => {
        setSelectedUsers(value);
    };

    const getCardImage = (skillId: string | null) => {
        const existSkill = applicationStore.skills.find(s => s.skillId == skillId)?.title || t("unknown_skill");

        if (existSkill == "unknown_skill") {
            return "/assets/img/test/Test.jpg";
        }
        else if (existSkill == "C# (Programming Language)") {
            return "/assets/img/test/CSharp.jpg";
        }
        else {
            return `/assets/img/test/${existSkill}.jpg`;
        }
    }

    const filterMultySkillOption: SelectProps['filterOption'] = (input, option) => {
        return option?.label?.toString().toLowerCase().includes(input.toLowerCase()) || false;
    };

    const handleDeleteTest = async (testId: number) => {
        const result = await testsStore.deleteById(testId.toString());

        if (result) {
            message.success(t("Success"));

            setFilteredCompanyTests(prevTests => prevTests.filter(test => test.id !== testId));
            setAllTest(prevTests => prevTests.filter(test => test.id !== testId));
        } else {
            message.error(t("add_test.DeleteFailed"));
        }
    };

    const navigateToPassage = async (testId: number) => {
        navigate(`/${applicationStore.company.Tenant}/tests/passage/${testId}`);
    }

    const renderTestCard = (test: Test, testAttempts?: TestAttempt[], userTestAssignment?: UserTestAssignment) => {
        const now = new Date();
        let lastAttemptDate = null;

        if (testAttempts) {
            lastAttemptDate = testAttempts?.length > 0
                ? new Date(Math.max(...testAttempts.map(ta => new Date(ta.finishDate).getTime())))
                : null;
        }

        const oneMonthLater = lastAttemptDate ? new Date(lastAttemptDate) : null;
        if (oneMonthLater) {
            oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
        }

        const isDisabled = lastAttemptDate && oneMonthLater && now < oneMonthLater;
        const availableDate = oneMonthLater ? oneMonthLater.toLocaleDateString() : null;

        const initialImageSrc = getCardImage(test.skillId?.toString() ?? "");
        const fallbackImage = "/assets/img/test/Test.jpg";

        return (
            <Card
                bordered
                style={{ width: 300, height: 400, margin: "10px", border: "1px solid #dcdcdc", position: "relative" }}
                hoverable
                actions={
                    applicationStore.user?.roles?.every(r => r.id !== Roles.TestManager && r.id !== Roles.Owner && r.id !== Roles.Admin)
                        ? [
                            <Button
                                key="start"
                                icon={<PlayCircleOutlined />}
                                type="primary"
                                size="middle"
                                onClick={() => navigateToPassage(test.id)}
                                disabled={isDisabled ?? false}
                            >
                                {isDisabled ? `${t("tests.availableOn")} ${availableDate}` : t("tests.passTestTitleButton")}
                            </Button>
                        ]
                        : test.isPublic === false
                            ? [
                                <Button
                                    key="start"
                                    icon={<PlayCircleOutlined />}
                                    type="primary"
                                    size="middle"
                                    onClick={() => navigateToPassage(test.id)}
                                />,
                                <Button
                                    key="purpose"
                                    icon={<UserAddOutlined />}
                                    type="primary"
                                    size="middle"
                                    style={{ backgroundColor: "green", borderColor: "green" }}
                                    onClick={() => showModal(test)}
                                />,
                                <Button key="edit" icon={<EditOutlined />} size="middle" onClick={() => handleEditNavigate(test.id)} />,
                                <Popconfirm
                                    key="delete"
                                    title={t("add_test.DeleteTestConfirmation")}
                                    onConfirm={async () => await handleDeleteTest(test.id)}
                                    okText={t("add_test.Confirm")}
                                    cancelText={t("add_test.Cancel")}
                                >
                                    <Button icon={<DeleteOutlined />} danger size="middle" />
                                </Popconfirm>
                            ]
                            : [
                                <Button
                                    key="start"
                                    icon={<PlayCircleOutlined />}
                                    type="primary"
                                    size="middle"
                                    onClick={() => navigateToPassage(test.id)}
                                />,
                                <Button
                                    key="purpose"
                                    icon={<UserAddOutlined />}
                                    type="primary"
                                    size="middle"
                                    style={{ backgroundColor: "green", borderColor: "green" }}
                                    onClick={() => showModal(test)}
                                />
                            ]
                }
            >
                <div style={{ width: "100%", height: "200px", marginBottom: "10px", overflow: "hidden" }}>
                    <img
                        src={initialImageSrc}
                        alt="test-image"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = fallbackImage;
                        }}
                    />
                </div>
                <Card.Meta
                    title={
                        test.name ||
                        applicationStore.skills.find(s => s.skillId == test.skillId?.toString())?.title ||
                        t("unknown_skill")
                    }
                    description={
                        <>
                            <p>{t("add_test.NumberQuestions")}: {test.questionsToProvideCount}</p>
                            <p>{t("add_test.Level")}: {test.level}</p>
                            {(test.language != null && test.language != "all") && (
                                <p>
                                    {t("tests.Language")}: {test.language == "en" ? t("add_test.English") : test.language == "ua" ? t("add_test.Ukrainian") : t("add_test.Russian")}
                                </p>
                            )}
                            <p>
                                {!isDisabled ? (
                                    <>
                                        {t("add_test.NumberAttempts")}: {applicationStore.user?.roles?.every(r => r.id !== Roles.TestManager) && userTestAssignment == null ? 1 : test.numberOfAttempts}
                                    </>
                                ) : (
                                    `${t("tests.availableOn")}: ${availableDate} `
                                )}
                            </p>
                            <p>{t("add_test.MaxTime")}: {test.maxTimeMinutes} {t("add_test.minutes")}</p>
                        </>
                    }
                />
            </Card>
        );
    };

    return (
        <Layout headerStyle={1} footerStyle={2} breadcrumbTitle={t("add_test.tests")}>

            {applicationStore.user?.roles?.some(r => (r.id === Roles.TestManager || r.id === Roles.Owner || r.id === Roles.Admin)) ? (
                <div style={{ margin: "20px" }}>
                    <Tabs defaultActiveKey="1" centered>
                        <TabPane tab={t("tests.allTests")} key="1" style={{ marginBottom: 50 }}>
                            <Spin spinning={loading} >
                                <div style={{ minHeight: "600px" }}>
                                    <div
                                        style={{
                                            margin: "20px",
                                            textAlign: "left",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                            width: '50%',
                                        }}
                                    >
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                navigate(`/${applicationStore.company.Tenant}/tests/create`);
                                            }}
                                            style={{ flexShrink: 0 }}
                                        >
                                            {t("add_test.create_test")}
                                        </Button>

                                        <Select
                                            mode="multiple"
                                            value={selectedLevels}
                                            style={{ color: "black", minWidth: "150px", flexGrow: 1 }}
                                            options={[
                                                { label: "Junior", value: "junior" },
                                                { label: "Middle", value: "middle" },
                                                { label: "Senior", value: "senior" }
                                            ]}
                                            onChange={handleLevelChange}
                                            dropdownStyle={{ color: "black" }}
                                        />

                                        <Select
                                            mode="multiple"
                                            value={selectedSkills}
                                            style={{ color: "black", minWidth: "150px", flexGrow: 2 }}
                                            options={applicationStore.skills.map(skill => ({
                                                label: skill.title,
                                                value: skill.skillId,
                                            }))}
                                            onChange={handleSkillChange}
                                            placeholder={t("tests.select_skill")}
                                            dropdownStyle={{ color: "black" }}
                                            allowClear
                                            filterOption={filterMultySkillOption}
                                            showSearch
                                        />
                                    </div>

                                    <Divider>{t("add_test.public_tests")}</Divider>
                                    <Row gutter={[8, 16]} justify="center" style={{ marginBottom: 60 }}>
                                        {filteredPublicTests.length > 0 ? (
                                            filteredPublicTests.slice(0, visibleTestsAllPublic).map(test => (
                                                <Col key={test.id} xs={22} sm={15} md={10} lg={6} xl={4} style={{ marginBottom: 20, marginRight: 30 }}>
                                                    {renderTestCard(test)}
                                                </Col>
                                            ))
                                        ) : (
                                            <p>{t("add_test.no_public_tests")}</p>
                                        )}
                                    </Row>
                                    {filteredPublicTests.length > visibleTestsAllPublic && (
                                        <div style={{ textAlign: 'right', marginTop: 20, marginRight: 110 }}>
                                            <Button type="primary" onClick={loadMoreAllPublicTests}>
                                                {t("tests.show_more")}
                                            </Button>
                                        </div>
                                    )}

                                    {filteredCompanyTests.length > 0 && (
                                        <Divider>{t("add_test.company_tests")}</Divider>
                                    )}

                                    {filteredCompanyTests.length > 0 && (
                                        <Row gutter={[8, 16]} justify="center" >
                                            {filteredCompanyTests.filter(t => !t.isDeleted).map(test => (
                                                <Col key={test.id} xs={22} sm={15} md={10} lg={6} xl={4} style={{ marginBottom: 20, marginRight: 30 }}>
                                                    {renderTestCard(test)}
                                                </Col>
                                            ))}
                                        </Row>
                                    )}
                                </div>
                            </Spin>
                        </TabPane>

                        <TabPane tab={t("tests.UserTests")} key="2">
                            <div style={{ padding: "20px" }}>
                                <TestUsersInfo
                                    companyAssignments={companyAssignments}
                                    setCompanyAssignments={setCompanyAssignments}
                                    userAssignments={userAssignments}
                                    setUserAssignments={setUserAssignments}
                                    allTests={allTest}
                                />
                            </div>
                        </TabPane>

                        <TabPane tab={t("tests.myTests")} key="3">
                            <div style={{ padding: "20px" }}>
                                <UserTestInfo
                                    loading={loading}
                                    setLoading={setLoading}
                                    companyAssignments={companyAssignments}
                                    setCompanyAssignments={setCompanyAssignments}
                                    userAssignments={userAssignments}
                                    setUserAssignments={setUserAssignments}
                                />
                            </div>

                            <div style={{ margin: "20px", marginBottom: "50px" }}>
                                <Row gutter={[8, 16]} justify="center">
                                    {userAssignments.length > 0 && (
                                        <Divider>{t("tests.assignedTests")}</Divider>
                                    )}
                                    {userAssignments.length > 0 && (
                                        userAssignments.filter(ua => ua.assignedToUserId.toString() == applicationStore.user.id)
                                            .filter(ua => ua.test != null)
                                            .filter(ua => ua.assignmentStatusId != AssignmentStatus.Completed)
                                            .filter(ua => ua.assignmentStatusId != AssignmentStatus.Expired)
                                            .map(ua => (
                                                <Col key={ua.test!.id} style={{ marginBottom: 20, marginRight: 30 }}>
                                                    {renderTestCard(ua.test!)}
                                                </Col>
                                            ))
                                    )}
                                </Row>
                            </div>
                        </TabPane>
                    </Tabs>
                </div>
            ) : (
                <div style={{ margin: "20px" }}>
                    <Tabs defaultActiveKey="1" centered>
                        <TabPane tab={t("tests.assignedTests")} key="1" style={{ marginBottom: 50 }}>
                            <Spin spinning={loading} style={{ minHeight: 600 }}>
                                <Row gutter={[16, 16]} justify="center">
                                    {(() => {
                                        const filteredAssignments = userAssignments
                                            .filter(ua => ua.test != null)
                                            .filter(ua => ua.assignmentStatusId !== AssignmentStatus.Completed)
                                            .filter(ua => ua.assignmentStatusId !== AssignmentStatus.Expired);

                                        if (filteredAssignments.length === 0) {
                                            return (
                                                <Col span={24} style={{ textAlign: "center", marginTop: 50 }}>
                                                    <Typography.Text type="secondary" style={{ fontSize: 16 }}>
                                                        {t("tests.noAssignedTests")}
                                                    </Typography.Text>
                                                </Col>
                                            );
                                        }

                                        return filteredAssignments.map(ua => (
                                            <Col key={ua.test!.id} xs={22} sm={10} md={6} lg={4} style={{ marginBottom: 20, marginRight: 30 }}>
                                                {renderTestCard(ua.test!, [], ua)}
                                            </Col>
                                        ));
                                    })()}
                                </Row>
                            </Spin>
                        </TabPane>

                        <TabPane tab={t("tests.Results")} key="2">
                            <div style={{ padding: "20px" }}>
                                <UserTestInfo
                                    loading={loading}
                                    setLoading={setLoading}
                                    companyAssignments={companyAssignments}
                                    setCompanyAssignments={setCompanyAssignments}
                                    userAssignments={userAssignments}
                                    setUserAssignments={setUserAssignments}
                                />
                            </div>
                        </TabPane>

                        <TabPane tab={t("tests.allTests")} key="3">
                            <Spin spinning={loading} style={{ minHeight: 600 }}>
                                <div style={{ padding: "20px" }}>
                                    <div style={{ margin: "20px", textAlign: "left", display: "flex", alignItems: "center", gap: "10px", width: '50%' }}>
                                        <Select
                                            mode="multiple"
                                            value={selectedLevels}
                                            style={{ color: "black", minWidth: "150px", flexGrow: 1 }}
                                            options={[
                                                { label: "Junior", value: "junior" },
                                                { label: "Middle", value: "middle" },
                                                { label: "Senior", value: "senior" }
                                            ]}
                                            onChange={handleLevelChange}
                                            dropdownStyle={{ color: "black" }}
                                        />

                                        <Select
                                            mode="multiple"
                                            value={selectedSkills}
                                            style={{ color: "black", minWidth: "150px", flexGrow: 2 }}
                                            options={applicationStore.skills.map(skill => ({
                                                label: skill.title,
                                                value: skill.skillId,
                                            }))}
                                            onChange={handleSkillChange}
                                            placeholder={t("tests.select_skill")}
                                            dropdownStyle={{ color: "black" }}
                                            allowClear
                                            filterOption={filterMultySkillOption}
                                            showSearch
                                        />
                                    </div>

                                    <Divider>{t("add_test.public_tests")}</Divider>
                                    <Row gutter={[8, 16]} justify="center" style={{ marginBottom: 60 }}>
                                        {filteredPublicTests.length > 0 ? (
                                            filteredPublicTests.slice(0, visibleTestsAllPublic).map(test => (
                                                <Col key={test.id} xs={22} sm={10} md={6} lg={4} style={{ marginBottom: 20, marginRight: 30 }}>
                                                    {renderTestCard(test)}
                                                </Col>
                                            ))
                                        ) : (
                                            <p>{t("add_test.no_public_tests")}</p>
                                        )}
                                    </Row>
                                    {filteredPublicTests.length > visibleTestsAllPublic && (
                                        <div style={{ textAlign: 'right', marginTop: 20, marginRight: 110 }}>
                                            <Button type="primary" onClick={loadMoreAllPublicTests}>
                                                {t("tests.show_more")}
                                            </Button>
                                        </div>
                                    )}

                                    {filteredCompanyTests.length > 0 && (
                                        <Divider>{t("add_test.company_tests")}</Divider>
                                    )}
                                    {filteredCompanyTests.length > 0 && (
                                        <Row gutter={[8, 16]} justify="center">
                                            {filteredCompanyTests.filter(t => !t.isDeleted).map(test => (
                                                <Col key={test.id} xs={22} sm={10} md={6} lg={4} style={{ marginBottom: 20, marginRight: 30 }}>
                                                    {renderTestCard(test)}
                                                </Col>
                                            ))}
                                        </Row>
                                    )}
                                </div>
                            </Spin>
                        </TabPane>
                    </Tabs>
                </div>
            )}


            <Modal
                key={currentLanguage}
                title={t("passage_test.test_details")}
                open={isModalVisible}
                onCancel={handleCancel}
                footer={loading ? null : [
                    <Button
                        key="assign"
                        type="primary"
                        onClick={handleAssign}
                    >
                        {t("tests.assign")}
                    </Button>,
                ]}
            >
                {loading ? (
                    <div>
                        <Spin size="small" spinning={loading}>{t("Loading...")}</Spin>
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <Text>{t("passage_test.topic")}: {applicationStore.skills.find(s => s.skillId == modalTest?.skillId?.toString())?.title || t("unknown_skill")}</Text>
                        <Text>{t("add_test.Level")}: {modalTest?.level}</Text>
                        <Text>{t("passage_test.number_of_attempts")}: {modalTest?.numberOfAttempts}</Text>
                        <Text>
                            <QuestionCircleOutlined /> {t("passage_test.number_of_questions")}: {modalTest?.questionsToProvideCount}
                        </Text>
                        <Text>
                            <ClockCircleOutlined /> {t("passage_test.time_limit")}: {modalTest?.maxTimeMinutes} {t("passage_test.minutes")}
                        </Text>
                        {(modalTest?.description != null && modalTest.description.length > 0) && (
                            <Text>{t("add_test.Description")}: {modalTest?.description}</Text>
                        )}

                        <Form layout="vertical" style={{ marginTop: "20px" }}>

                            <Form.Item label={t("tests.selectUsers")}>
                                <Select
                                    mode="multiple"
                                    value={selectedUsers}
                                    style={{ width: "100%", height: "auto", overflow: 'auto' }}
                                    onChange={handleUserChange}
                                    filterOption={(input, option) => {
                                        if (option && option.label) {
                                            const userEmail = String(option.label).toLowerCase();
                                            const searchText = input.toLowerCase();
                                            return userEmail.includes(searchText);
                                        }
                                        return false;
                                    }}
                                    optionLabelProp="label"
                                >
                                    {applicationStore.users.map((user) => (
                                        <Select.Option key={user.id} value={user.id} label={user.email}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                <img
                                                    src={user.photoId
                                                        ? `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/w_300/${user.photoId}`
                                                        : `https://res.cloudinary.com/dnl3x07wo/image/upload/w_300/z03w7hlsy8hcoic9dlcn`}
                                                    alt="avatar"
                                                    style={{
                                                        width: "30px",
                                                        height: "30px",
                                                        borderRadius: "50%",
                                                        objectFit: "cover",
                                                    }}
                                                />
                                                <div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        {user.firstName} {user.lastName}
                                                    </div>
                                                    <span>{user.email}</span>
                                                </div>
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label={t("tests.selectLanguage")}>
                                        <Select
                                            style={{ width: "100%", height: "auto", overflow: 'auto' }}
                                            onChange={handleLanguageChange}
                                            disabled={modalTest?.language !== "all"}
                                            value={selectedLanguage}
                                        >
                                            {(modalTest?.language === 'all' ? [Languages.ANY, Languages.EN, Languages.RU, Languages.UK] : [modalTest?.language]).map(lang => (
                                                <Select.Option key={lang} value={lang}>
                                                    {t(`languages.${lang}`)}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label={t("tests.selectDeadlineDate")} style={{ marginBottom: 0 }}>
                                        <div style={{ display: "flex", gap: "10px" }}>
                                            <DatePicker
                                                key={currentLanguage}
                                                locale={dateLocale}
                                                value={selectedDate}
                                                style={{ width: "100%" }}
                                                required
                                                onChange={(value) => setSelectedDate(value)}
                                                disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
                                            />
                                        </div>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                )}
            </Modal>
        </Layout >
    );
});

export default Tests;